// Presentation shared by the single-target page and the bulk run panel, so
// both render a step status, an input and a claimed step the same way.

import type { OnboardingStepStatus } from '../../../api/onboardingTypes'

export const STATUS_TONE: Record<OnboardingStepStatus, string> = {
  pending: 'border-border bg-surface-2 text-muted-foreground',
  claimed: 'border-primary/40 bg-primary/10 text-primary',
  running: 'border-primary/40 bg-primary/10 text-primary',
  succeeded: 'border-success/40 bg-success/10 text-success',
  failed: 'border-destructive/40 bg-destructive/10 text-destructive',
  skipped: 'border-border bg-surface-2 text-muted-foreground',
  delivery_unknown: 'border-warning/40 bg-warning/10 text-warning',
}

export const INPUT_CLASS =
  'h-9 rounded-lg border border-input-border bg-input-bg px-3 text-sm text-foreground placeholder:text-text-muted focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20'

export const PRIMARY_BUTTON_CLASS =
  'h-9 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground disabled:cursor-not-allowed disabled:bg-primary/40'

export const SMALL_PRIMARY_BUTTON_CLASS =
  'h-7 rounded-lg bg-primary px-3 text-xs font-medium text-primary-foreground disabled:cursor-not-allowed disabled:bg-primary/40'

export const SECONDARY_BUTTON_CLASS =
  'h-7 cursor-pointer rounded-lg border border-input-border bg-surface-1 px-3 text-xs text-foreground hover:bg-surface-3 disabled:cursor-not-allowed disabled:opacity-50'

// A claimed step is only offered for recovery once it is this old; the server
// enforces the real conditions (executor verifiably gone), this just keeps the
// button from appearing for a step that is simply still running.
const RECOVER_OFFER_AFTER_MINUTES = 10
// Matches the server's heartbeat staleness threshold (onboarding_state).
const HEARTBEAT_STALE_MINUTES = 3

/** Whole minutes since an ISO timestamp, or null if absent/unparseable. */
export function minutesSince(iso: string | null | undefined, now: number = Date.now()): number | null {
  if (!iso) return null
  const at = Date.parse(iso)
  if (Number.isNaN(at)) return null
  return Math.max(0, Math.floor((now - at) / 60000))
}

/** How a claimed/running step should be presented. Pure, for tests. */
export function claimedStepView(
  claimedAt: string | null | undefined,
  heartbeatAt: string | null | undefined,
  now: number = Date.now(),
): { claimedMinutes: number | null; interrupted: boolean; canOfferRecover: boolean } {
  const claimedMinutes = minutesSince(claimedAt, now)
  // Before the first heartbeat, the claim time is the last sign of life.
  const quietMinutes = minutesSince(heartbeatAt ?? claimedAt, now)
  return {
    claimedMinutes,
    interrupted: quietMinutes !== null && quietMinutes >= HEARTBEAT_STALE_MINUTES,
    canOfferRecover: claimedMinutes !== null && claimedMinutes >= RECOVER_OFFER_AFTER_MINUTES,
  }
}
